import type { QualityStatistics } from '../../../../schemas/performanceKpis.schemas';

export const GENERAL_QUALITY_METRICS = [
    { key: 'addedReports', label: 'Missing Reports' },
    { key: 'removedReports', label: 'Excess Reports' },
    { key: 'addedCoreValues', label: 'Missing Core Values' },
    { key: 'changedCoreValues', label: 'Wrong Core Values' },
    { key: 'removedCoreValues', label: 'Excess Core Values' },
    { key: 'addedLabels', label: 'Missing Labels' },
    { key: 'changedLabels', label: 'Wrong Labels' },
    { key: 'removedLabels', label: 'Excess Labels' },
    { key: 'dataAddedBecauseOfNewDescriptor', label: 'Missing Model Fields' },
    { key: 'dataRemovedBecauseOfRemovedDescriptor', label: 'Excess Model Fields' },
    { key: 'affectedRecords', label: 'Affected Records' },
] as const;

export const CORE_VALUE_CHANGE_METRICS = [
    { key: 'changedCoreValues3dCalculated', label: 'Changed (3D Calculated)' },
    { key: 'changedCoreValuesSign', label: 'Changed (Sign)' },
    { key: 'changedCoreValuesScale', label: 'Changed (Scale)' },
    { key: 'changedCoreValuesRounding', label: 'Changed (Rounding)' },
    { key: 'changedCoreValuesGeneral', label: 'Changed (General)' },
    { key: 'addedCoreValues3dCalculated', label: 'Missing (3D Calculated)' },
    { key: 'removedCoreValues3dCalculated', label: 'Excess (3D Calculated)' },
] as const;

export const QUALITY_METRICS = [
    ...GENERAL_QUALITY_METRICS.filter(metric => metric.key !== 'affectedRecords'),
    ...CORE_VALUE_CHANGE_METRICS,
    { key: 'affectedRecords', label: 'Affected Records' },
] as const;

export type QualityBreakdown = {
    [Key in typeof QUALITY_METRICS[number]['key']]: { total: number; per100Cells: number | undefined };
};

export function createQualityStatistics(): QualityStatistics {
    return {
        addedReports: 0,
        removedReports: 0,
        addedCoreValues: 0,
        changedCoreValues: 0,
        removedCoreValues: 0,
        addedLabels: 0,
        changedLabels: 0,
        removedLabels: 0,
        dataAddedBecauseOfNewDescriptor: 0,
        dataRemovedBecauseOfRemovedDescriptor: 0,
        coreValueChangeKindCounts: {
            changedCoreValues3dCalculated: 0,
            changedCoreValuesSign: 0,
            changedCoreValuesScale: 0,
            changedCoreValuesRounding: 0,
            changedCoreValuesGeneral: 0,
            addedCoreValues3dCalculated: 0,
            removedCoreValues3dCalculated: 0,
        },
        affectedRecords: 0,
    };
}

export function accumulateQualityStatistics(target: QualityStatistics, source: QualityStatistics) {
    for (const { key } of GENERAL_QUALITY_METRICS) {
        target[key] += source[key];
    }
    for (const { key } of CORE_VALUE_CHANGE_METRICS) {
        target.coreValueChangeKindCounts[key] += source.coreValueChangeKindCounts[key];
    }
}

export function calculateQualityBreakdown(cellsScope: number, statistics: QualityStatistics): QualityBreakdown {
    function calculate(total: number) {
        return { total, per100Cells: cellsScope === 0 ? undefined : (total / cellsScope) * 100 };
    }

    return {
        addedReports: calculate(statistics.addedReports),
        removedReports: calculate(statistics.removedReports),
        addedCoreValues: calculate(statistics.addedCoreValues),
        changedCoreValues: calculate(statistics.changedCoreValues),
        removedCoreValues: calculate(statistics.removedCoreValues),
        addedLabels: calculate(statistics.addedLabels),
        changedLabels: calculate(statistics.changedLabels),
        removedLabels: calculate(statistics.removedLabels),
        dataAddedBecauseOfNewDescriptor: calculate(statistics.dataAddedBecauseOfNewDescriptor),
        dataRemovedBecauseOfRemovedDescriptor: calculate(statistics.dataRemovedBecauseOfRemovedDescriptor),
        changedCoreValues3dCalculated: calculate(statistics.coreValueChangeKindCounts.changedCoreValues3dCalculated),
        changedCoreValuesSign: calculate(statistics.coreValueChangeKindCounts.changedCoreValuesSign),
        changedCoreValuesScale: calculate(statistics.coreValueChangeKindCounts.changedCoreValuesScale),
        changedCoreValuesRounding: calculate(statistics.coreValueChangeKindCounts.changedCoreValuesRounding),
        changedCoreValuesGeneral: calculate(statistics.coreValueChangeKindCounts.changedCoreValuesGeneral),
        addedCoreValues3dCalculated: calculate(statistics.coreValueChangeKindCounts.addedCoreValues3dCalculated),
        removedCoreValues3dCalculated: calculate(statistics.coreValueChangeKindCounts.removedCoreValues3dCalculated),
        affectedRecords: calculate(statistics.affectedRecords),
    };
}
